import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import VideoCard from '@kidstube/components/VideoCard';
import ChannelPills from '@kidstube/components/ChannelPills';
import type { YouTubeVideo, YouTubeChannel, ContinueWatchingItem } from '@kidstube/types';
import { apiFetch } from '@kidstube/lib/api';
import { decodeHtmlEntities } from '@kidstube/lib/text';

export default function HomePage() {
  const { channelId } = useParams<{ channelId?: string }>();
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [channels, setChannels] = useState<YouTubeChannel[]>([]);
  const [continueWatching, setContinueWatching] = useState<ContinueWatchingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Initial data fetch
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      setVideos([]);
      setHasMore(true);

      try {
        // Fetch channels, continue watching, and videos in parallel
        const [channelsData, continueData, videosData] = await Promise.all([
          apiFetch<{ channels?: YouTubeChannel[] }>('/api/channels'),
          apiFetch<{ continueWatching?: ContinueWatchingItem[] }>('/api/user/continue-watching'),
          apiFetch<{ videos?: YouTubeVideo[]; hasMore?: boolean }>(
            channelId ? `/api/videos?channelId=${channelId}` : '/api/videos'
          ),
        ]);

        setChannels(channelsData.channels || []);
        setContinueWatching(continueData.continueWatching || []);
        setVideos(videosData.videos || []);
        setHasMore(videosData.hasMore ?? false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [channelId]);

  // Load more videos (infinite scroll)
  const loadMoreVideos = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const offset = videos.length;
      const url = channelId 
        ? `/api/videos?channelId=${channelId}&offset=${offset}&loadMore=true`
        : `/api/videos?offset=${offset}&loadMore=true`;
      
      const data = await apiFetch<{ videos?: YouTubeVideo[]; hasMore?: boolean }>(url);
      setVideos(prev => [...prev, ...(data.videos || [])]);
      setHasMore(data.hasMore ?? false);
    } catch (err) {
      console.error('Error loading more videos:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [videos.length, channelId, loadingMore, hasMore]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreVideos();
        }
      },
      { rootMargin: '200px' }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, loadMoreVideos]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">📺</div>
        <div className="loading-text">Finding awesome videos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">😕</div>
        <h2 className="empty-state-title">Oops!</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Only show header when viewing a specific channel */}
      {channelId && (
        <div className="page-header">
          <h1 className="page-title">
            {decodeHtmlEntities(channels.find((c) => c.id === channelId)?.title || 'Channel')}
          </h1>
          <p className="page-description">Latest videos from this channel</p>
        </div>
      )}

      <ChannelPills channels={channels} activeChannelId={channelId} />

      {/* Continue Watching Section */}
      {!channelId && continueWatching.length > 0 && (
        <div className="continue-watching-section">
          <h2 className="section-title">▶️ Continue Watching</h2>
          <div className="continue-watching-scroll">
            {continueWatching.map((item) => (
              <Link 
                key={item.videoId} 
                to={`/watch/${item.videoId}`}
                className="continue-card"
              >
                <div className="continue-thumb">
                  {item.thumbnail && <img src={item.thumbnail} alt="" />}
                  <div 
                    className="continue-progress-bar"
                    style={{ 
                      width: `${Math.min(100, (item.position / item.duration) * 100)}%` 
                    }}
                  />
                </div>
                <div className="continue-info">
                  <div className="continue-title">{decodeHtmlEntities(item.title)}</div>
                  <div className="continue-meta">
                    {decodeHtmlEntities(item.channelTitle)} • {formatTimeRemaining(item.duration - item.position)} left
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {videos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎬</div>
          <h2 className="empty-state-title">No videos yet!</h2>
          <p>Ask a parent to add some channels.</p>
        </div>
      ) : (
        <>
          <div className="video-grid">
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {/* Infinite scroll trigger */}
          <div ref={loadMoreRef} className="load-more-trigger">
            {loadingMore && (
              <div className="loading-more">
                <div className="loading-spinner" />
                <span>Loading more videos...</span>
              </div>
            )}
            {!hasMore && videos.length > 0 && (
              <div className="end-of-list">
                <span>🎉 You've seen all the videos!</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function formatTimeRemaining(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}
